import {
  AVIFCommandSubText,
  AVIFCommandText,
  AVIFDefaultText,
  AVIFSubHeadText,
  AVIFTableHeaderText,
  AVIFTableText,
  GRAY30,
  SB,
} from 'global-styles';
import React from 'react';
import styled from 'styled-components';

const AVIFDetailCodec = () => {
  return (
    <AVIFDetailCodecContainer>
      <CodecDetailSubTitle>Codec</CodecDetailSubTitle>
      <table>
        <thead>
          <tr>
            <StyledTableHeaderText as="th">Codec</StyledTableHeaderText>
            <StyledTableHeaderText as="th">Code base</StyledTableHeaderText>
            <StyledTableHeaderText as="th">Hash</StyledTableHeaderText>
          </tr>
        </thead>
        <tbody>
          <tr>
            <StyledTableText as="td">
              <p>libjpeg-turbo</p>
            </StyledTableText>
            <StyledTableText as="td">
              <p>
                <span>a JPEG codec to accelerate libjpeg by 2-6x</span>
                <br />
                <a
                  href="https://github.com/libjpeg-turbo/libjpeg-turbo"
                  target="_blank"
                  rel="noreferrer"
                >
                  <AVIFStrongText>
                    <span className="under-l">
                      https://github.com/libjpeg-turbo/libjpeg-turbo
                    </span>
                  </AVIFStrongText>
                </a>
              </p>
            </StyledTableText>
            <StyledTableText as="td">
              <p>a01857cff6a2b7b3283ef1d0ecb6b01d06b597f8</p>
            </StyledTableText>
          </tr>
          <tr>
            <StyledTableText as="td">
              <p>avifenc</p>
            </StyledTableText>
            <StyledTableText as="td">
              <div>
                <a
                  href="https://github.com/AOMediaCodec/libavif"
                  target="_blank"
                  rel="noreferrer"
                >
                  <AVIFStrongText>
                    <span className="under-l">
                      https://github.com/AOMediaCodec/libavif
                    </span>
                  </AVIFStrongText>
                </a>{' '}
                v0.11.1
                <ul>
                  <li>aom: v3.5.0</li>
                  <li>svt: v1.3.0</li>
                </ul>
              </div>
            </StyledTableText>
            <StyledTableText as="td">
              <p>6ab53189045e7a6fe0bd93d14977b2a4f8efa5e9</p>
            </StyledTableText>
          </tr>
          <tr>
            <StyledTableText as="td">
              <p>pulsar-avif</p>
            </StyledTableText>
            <StyledTableText as="td">
              <p>BLUEDOT&apos;s FPGA-based AVIF encoder</p>
            </StyledTableText>
            <StyledTableText as="td">
              <p>5307f4605513110ad3c267fac6873e1deb913385</p>
            </StyledTableText>
          </tr>
        </tbody>
      </table>
      <CodecDetailSubTitle style={{ marginTop: '24px' }}>
        Command line
      </CodecDetailSubTitle>
      <div className="command-area">
        <AVIFCommandText className="c-t">libjpeg-turbo</AVIFCommandText>
        <AVIFCommandSubText className="c-t c-t-s-t">
          <FakeDisc />
          {
            './cjpeg -quality ${quality} -sample 2x2 -outfile ${output jpg} ${input ppm}'
          }
        </AVIFCommandSubText>

        <AVIFCommandText className="c-t">avifenc with AOM</AVIFCommandText>
        <AVIFCommandSubText className="c-t  c-t-s-t">
          <FakeDisc />
          {
            './avifenc ${input png} --min ${qp} --max ${qp} -y 420 -d 8 -j ${thread} -r l -c aom ${output avif}'
          }
        </AVIFCommandSubText>

        <AVIFCommandText className="c-t">avifenc with SVT</AVIFCommandText>
        <AVIFCommandSubText className="c-t  c-t-s-t">
          <FakeDisc />
          {
            './avifenc ${input png} --min ${qp} --max ${qp} -y 420 -d 8 -j ${thread} -r l -c svt ${output avif}'
          }
        </AVIFCommandSubText>

        <AVIFCommandText className="c-t">pulsar-avif</AVIFCommandText>
        <AVIFCommandSubText className="c-t  c-t-s-t">
          <FakeDisc />
          {
            './avif_sp_client -i ${input yuv} -o ${output avif} --width ${width} --height ${height} --enable-avif 1 --avif-seq 0 --fps 1 --frames 1 --qp ${qp}'
          }
        </AVIFCommandSubText>
      </div>
      <div className="command-sub-area">
        <AVIFDefaultText>
          The test images are in the format of jpg. For the evaluation, we used
          decoded images of them. Therefore, the speed measured here does not
          include a time for decoding jpg and converting color space.
        </AVIFDefaultText>
      </div>
      <div className="test-imgs-area tt-w">
        <CodecDetailSubTitle>Test Images</CodecDetailSubTitle>
        <div className="tt-w-inner-w">
          <AVIFDefaultText>
            https://drive.google.com/drive/folders/1VK12pBFnYJzJRD1k8uo38yammO-_XtkO
          </AVIFDefaultText>
          <ul>
            <AVIFDefaultText as="li">
              Kodak - 24 images, 768x512;{' '}
              <a
                href="https://r0k.us/graphics/kodak/"
                target="_blank"
                rel="noreferrer"
              >
                <AVIFStrongText>
                  <span className="under-l">
                    Kodak Lossless True Color Image Suite
                  </span>
                </AVIFStrongText>
              </a>
            </AVIFDefaultText>
            <AVIFDefaultText as="li">
              CLIC - 303 images, 2048x1320; from workshop and challenges on
              learned image compression(CLIC)
            </AVIFDefaultText>
          </ul>
        </div>
      </div>

      <div className="qmace-area tt-w">
        <CodecDetailSubTitle>
          Quality Metrics and Coding Efficiency
        </CodecDetailSubTitle>
        <div className="tt-w-inner-w">
          <AVIFDefaultText>BD-Rate for the following metrics:</AVIFDefaultText>
          <ul>
            <AVIFDefaultText as="li">PSNR_Y</AVIFDefaultText>
            <AVIFDefaultText as="li">
              PSNR_AVG: (PSNR_Y * 4 + PSNR_U + PSNR_V) / 6
            </AVIFDefaultText>
            <AVIFDefaultText as="li">SSIM</AVIFDefaultText>
            <AVIFDefaultText as="li">VIF</AVIFDefaultText>
          </ul>
          <AVIFDefaultText>
            BD-Rate: G. Bjontegaard, Calculation of average PSNR differences
            between RD-curves (VCEG-M33)
          </AVIFDefaultText>
        </div>
      </div>
    </AVIFDetailCodecContainer>
  );
};

export default AVIFDetailCodec;

export const AVIFDetailCommonContainer = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  color: #313131;
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    text-align: left;
  }
  thead th {
    background: #f2f2f2;
    width: 33.33%;
    padding: 10px 16px;
  }
  td {
    padding: 10px 16px;
    width: 33.33%;
    word-break: break-word;
  }
  td p {
    min-height: 80px;
  }
  tr {
    border: 1px solid #d4d4d4;
  }
  th + th {
    border-left: 1px solid #d4d4d4;
  }
  td + td {
    border-left: 1px solid #d4d4d4;
  }
  ul {
    padding-left: 28px;
    list-style: disc;
  }
  table.t-f-th thead th:first-of-type {
    background: transparent;
  }
  table.t-f-th tbody td:first-of-type {
    text-align: left;
  }
  table.t-f-th tbody td {
    text-align: right;
  }

  @media screen and (max-width: 599px) {
    thead th,
    td {
      padding: 10px;
    }
    ul {
      padding-left: 20px;
    }
  }
`;

export const AVIFStrongText = styled(AVIFDefaultText)`
  color: ${SB};
  font-weight: 500;
  /* margin: 0 0 24px; */
  line-height: 210%;
  /* display: inline-block; */

  .under-l {
    text-decoration: underline;
    font-weight: 700;
  }
`;

const AVIFDetailCodecContainer = styled(AVIFDetailCommonContainer)`
  margin-top: 48px;
  .command-area {
    background: #f2f2f2;
    display: flex;
    flex-direction: column;
    padding: 32px;
    margin-bottom: 24px;
  }
  .c-t + .c-t {
    margin-top: 15px;
  }
  .c-t-s-t {
    color: #313131;
  }

  .c-t.c-t-s-t {
    color: #4d4d4d;
  }

  .command-sub-area {
    padding: 16px 32px;
    border-top: 1px solid #b0b0b0;
    border-bottom: 1px solid #b0b0b0;
    margin-bottom: 24px;
  }
  .tt-w + .tt-w {
    margin-top: 24px;
  }
  .tt-w {
    color: #313131;
  }
  .tt-w-inner-w {
    padding: 16px 32px;
  }
`;

export const CodecDetailSubTitle = styled(AVIFSubHeadText)`
  margin-bottom: 24px;
  display: block;
  color: ${GRAY30};
`;

export const StyledTableHeaderText = styled(AVIFTableHeaderText)``;

export const StyledTableText = styled(AVIFTableText)``;

const FakeDisc = styled.span`
  width: 5px;
  height: 5px;
  background: #313131;
  border-radius: 50%;
  display: inline-block;
  margin: 0 8px 2px 0;
`;
